import { Controller } from "@hotwired/stimulus"

// Pins the chat drawer's message list to the bottom while content
// streams in, but stays put if the user has scrolled up to read.
//
// Key idea: stickiness is decided from SCROLL events, not from the
// post-mutation distance. The previous version checked
// `scrollHeight - scrollTop - clientHeight <= threshold` inside the
// MutationObserver, but a single chunk can grow scrollHeight by more
// than the threshold in one tick — which made the controller falsely
// conclude the user had scrolled up, and stop following. Tracking
// stickiness via the scroll listener (where the user's intent shows
// up) and only consulting the flag on mutation avoids that.
export default class extends Controller {
  static values = { threshold: { type: Number, default: 80 } }

  connect() {
    // Start pinned — we open the drawer at the bottom.
    this.stickToBottom = true

    this.onScroll = () => this.updateStickiness()
    this.element.addEventListener("scroll", this.onScroll, { passive: true })

    // Observe the messages list for any DOM growth: new messages from
    // `broadcasts_to inserts_by: :append` (childList) and streamed text
    // chunks appended into `message_<id>_content` divs by
    // `Message#broadcast_append_chunk` (childList + characterData).
    const messages = this.element.querySelector("#messages") || this.element
    this.observer = new MutationObserver(() => this.handleMutation())
    this.observer.observe(messages, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    this.scrollToBottom()
  }

  disconnect() {
    this.observer?.disconnect()
    this.element.removeEventListener("scroll", this.onScroll)
  }

  updateStickiness() {
    const distance =
      this.element.scrollHeight - this.element.scrollTop - this.element.clientHeight
    this.stickToBottom = distance <= this.thresholdValue
  }

  handleMutation() {
    if (this.stickToBottom) this.scrollToBottom()
  }

  scrollToBottom() {
    // rAF runs after layout for the mutation that triggered us, so
    // scrollHeight reflects the chunk we just appended.
    requestAnimationFrame(() => {
      this.element.scrollTop = this.element.scrollHeight
    })
  }
}
